/**
 * Event reminder planner — pure scheduling arithmetic, no runtime attached.
 *
 * This module answers four questions and nothing else:
 *
 *   1. WHO gets reminded about an event        → {@link shouldRemind}
 *   2. WHEN does that event actually start     → {@link reminderStartMs}
 *   3. WHICH reminders exist for a given lead  → {@link planReminders}
 *   4. WHICH of them are due / already missed  → {@link dueNow} / {@link missed}
 *
 * It deliberately imports nothing at runtime — no DOM, no Capacitor, no Pinia,
 * no storage — so `scripts/verify-events.mjs` can bundle it and drive it against
 * a fake clock offline (the stub-bundle pattern used by
 * `scripts/verify-mappers.mjs`; there is no test runner in this checkout). The
 * only import is type-only and erases at build time.
 *
 * The stateful half — storage, the metronome, the network refresh and the
 * native hand-off — lives in `event-reminders.ts`.
 */
import type { EventVM, ReminderLead, ReminderUnit } from '@/types/event';

/**
 * Minutes per unit letter. ⚠ Mirrors `events_reminder_lead_units()` on the server.
 */
export const LEAD_UNIT_MINUTES: Readonly<Record<ReminderUnit, number>> = {
  m: 1,
  h: 60,
  d: 24 * 60,
};

/**
 * The longest lead a user may choose, in minutes (7 days).
 *
 * ⚠ MIRRORS THE SERVER CONSTANT `EVENTS_REMINDER_MAX_LEAD_MINUTES`. Server-side it is
 * also the cron sweep's scan breadth, so a client that offered more would plan an
 * in-app banner for a time the ERPat bell notification can never reach — and the PATCH
 * would 422 anyway.
 */
export const MAX_LEAD_MINUTES = 7 * 24 * 60;

/** The largest whole amount that fits under {@link MAX_LEAD_MINUTES} for each unit. */
export const LEAD_UNIT_MAX: Readonly<Record<ReminderUnit, number>> = {
  m: MAX_LEAD_MINUTES,
  h: MAX_LEAD_MINUTES / 60,
  d: MAX_LEAD_MINUTES / (24 * 60),
};

/**
 * The one-tap presets a picker offers. ⚠ Mirrors `events_reminder_leads()`.
 *
 * A CONVENIENCE LIST, NOT THE ACCEPTED SET — validating against it is exactly what made
 * this preference a three-value enum. {@link parseLead} is what validates.
 */
export const LEAD_PRESETS: readonly ReminderLead[] = [
  'off', '10m', '20m', '30m', '1h', '2h', '1d', '2d', '7d',
];

/** The server's default when a user has never chosen. ⚠ Mirrors the API contract. */
export const DEFAULT_LEAD: ReminderLead = '1d';

/** The parts of a lead. `off` has no amount or unit — it is the absence of a lead. */
export interface LeadParts {
  lead: ReminderLead;
  /** Whole and >= 1. `0` only for `off`. */
  amount: number;
  unit: ReminderUnit | 'off';
  /** `0` for `off`, which is what makes it fall out of {@link planReminders}. */
  minutes: number;
}

/**
 * Parse a lead token into its parts, or `null` when it is not a valid lead.
 *
 * ⚠ MIRRORS `events_parse_reminder_lead()` ON THE SERVER, including its rejections.
 * The server is the authority — it schedules the bell notification and 422s a bad
 * value — so anything this accepts that the server does not is a lead the user appears
 * to have saved but has not.
 *
 * Rejects rather than coerces: `1.5h`, `-2d`, `20 m`, `1e3m` and anything over
 * {@link MAX_LEAD_MINUTES} all return `null`. A coerced lead reminds at a time the user
 * did not choose and cannot see.
 *
 * `null` is the ONLY invalid signal; callers decide what to do with it, and they
 * deliberately differ — a picker refuses to save, while a read path falls back to
 * {@link DEFAULT_LEAD} so a corrupted stored value never silently stops reminding.
 */
export function parseLead(value: unknown): LeadParts | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;

  const token = String(value).trim().toLowerCase();
  if (token === 'off') return { lead: 'off', amount: 0, unit: 'off', minutes: 0 };

  // Anchored and integer-only, matching the server's /^([0-9]{1,5})([mhd])$/.
  const match = /^([0-9]{1,5})([mhd])$/.exec(token);
  if (!match) return null;

  const amount = Number(match[1]);
  const unit = match[2] as ReminderUnit;
  if (!Number.isInteger(amount) || amount < 1) return null;

  const minutes = amount * LEAD_UNIT_MINUTES[unit];
  if (minutes > MAX_LEAD_MINUTES) return null;

  // Rebuilt from the parsed parts rather than echoed, so '007h' canonicalises to '7h'
  // — the same normalisation the server applies before storing.
  return { lead: `${amount}${unit}`, amount, unit, minutes };
}

/** Build a lead token from an amount and unit. `null` when the pair is out of bounds. */
export function makeLead(amount: number, unit: ReminderUnit): ReminderLead | null {
  return parseLead(`${Math.trunc(amount)}${unit}`)?.lead ?? null;
}

/**
 * Lead time in minutes, or `null` for "no reminder at all".
 *
 * An unreadable lead resolves to the documented default rather than to `off`, matching
 * `events_reminder_lead_minutes()`: a broken preference must never silently stop
 * reminding somebody.
 */
export function leadMinutes(lead: ReminderLead): number | null {
  const parsed = parseLead(lead) ?? parseLead(DEFAULT_LEAD);
  const minutes = parsed?.minutes ?? 0;
  return minutes > 0 ? minutes : null;
}

/**
 * How late a reminder may still be shown.
 *
 * A phone that was asleep, a laptop lid that was shut, a tab the browser froze
 * — any of these can leave a reminder hours past its fire time. Showing it then
 * is noise ("your 9am stand-up starts in 20 minutes" at 4pm), so anything older
 * than this is marked fired SILENTLY by {@link missed} instead.
 */
export const REMINDER_GRACE_MS = 30 * 60 * 1000;

/**
 * The civil time an all-day event is treated as starting at, for reminder
 * purposes only — 09:00 local, expressed as minutes past midnight.
 *
 * ⚠ MIRRORS THE SERVER CONSTANT `EVENTS_REMINDER_ALL_DAY_ANCHOR = "09:00:00"`.
 * The two MUST stay equal: the server cron sends the ERPat bell notification and
 * this module drives the in-app banner and the Android notification, so a drift
 * here means the same event reminds one user at two different times from two
 * different channels.
 *
 * Without an anchor an all-day event starts at midnight, which makes "1 day
 * before" fire at the PREVIOUS midnight and "20 minutes before" fire at 23:40
 * the night before — both useless.
 */
export const REMINDER_ALL_DAY_ANCHOR_MIN = 540;

/** One planned reminder for one occurrence of one event, at one lead setting. */
export interface ReminderEntry {
  /** {@link reminderKey} — stable identity across replans. */
  key: string;
  eventId: string;
  /** `YYYY-MM-DD` of this occurrence (a recurring series has many). */
  occurrenceDate: string;
  title: string;
  /** Epoch ms the event starts (all-day events use the anchor above). */
  startAt: number;
  /** Epoch ms the reminder should surface — `startAt` minus the lead. */
  fireAt: number;
  /** Already surfaced (or deliberately silenced as missed). Never re-fires. */
  fired: boolean;
  /** Deterministic 32-bit id for the native notification — {@link nativeIdOf}. */
  nativeId: number;
}

/**
 * Identity of a reminder.
 *
 * The lead is part of the key on purpose: changing the preference produces a
 * completely different key set, so the old entries fall out of the plan and the
 * runtime cancels their native notifications instead of leaving a 1-day
 * reminder armed after the user switched to 20 minutes.
 */
export function reminderKey(eventId: string, occurrenceDate: string, lead: ReminderLead): string {
  return `${eventId}@${occurrenceDate}#${lead}`;
}

/**
 * A positive 32-bit id derived from the reminder key (FNV-1a).
 *
 * `LocalNotifications` ids must be Java ints, and schedule/cancel are matched by
 * id alone. Hashing the key rather than allocating a counter makes the mapping
 * reproducible from the key itself, so a replan can cancel a notification it
 * did not personally schedule — no id table to persist, and nothing stranded
 * when storage is cleared.
 *
 * Masked to 31 bits so it is never negative, and never `0` (some Android shells
 * treat id 0 as "no id").
 */
export function nativeIdOf(key: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    // Math.imul keeps the 32-bit FNV prime multiply exact — a plain `*` would
    // lose the low bits to float64 rounding once the product exceeds 2^53.
    hash = Math.imul(hash, 0x01000193);
  }
  const id = hash & 0x7fffffff;
  return id === 0 ? 1 : id;
}

/**
 * Whether this user should be reminded about this event at all.
 *
 * Mirrors the server's audience rule exactly: when RSVP is enabled the reminder
 * is for people who said they are going; when it is disabled (an announcement,
 * a holiday, a training everyone is simply expected at) everyone the event is
 * shared with gets it. Diverging here would have the banner, the bell and the
 * device notification disagree about who is being reminded.
 */
export function shouldRemind(event: EventVM): boolean {
  if (event.rsvp.enabled) return event.rsvp.myStatus === 'going';
  return true;
}

/** `YYYY-MM-DD` → `[year, month, day]`, or null when it is not a plain date. */
function parseCivilDate(value: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return [year, month, day];
}

/**
 * The instant an event starts, in epoch ms, as reminders should understand it.
 *
 * Timed events use the view model's own `startMs`. All-day events have no clock
 * time, so they are anchored to {@link REMINDER_ALL_DAY_ANCHOR_MIN} past civil
 * midnight of the occurrence — built from the calendar fields rather than by
 * adding milliseconds to a midnight timestamp, so it lands on the local 09:00
 * even on a day that gains or loses an hour to DST.
 *
 * Returns `NaN` when neither path yields a usable instant; callers skip those.
 */
export function reminderStartMs(event: EventVM): number {
  if (event.allDay) {
    const civil = parseCivilDate(event.occurrenceDate) ?? parseCivilDate(event.startDate);
    if (civil) {
      const [year, month, day] = civil;
      const hours = Math.floor(REMINDER_ALL_DAY_ANCHOR_MIN / 60);
      const minutes = REMINDER_ALL_DAY_ANCHOR_MIN % 60;
      return new Date(year, month - 1, day, hours, minutes, 0, 0).getTime();
    }
    // No parsable date — fall through to whatever the mapper computed.
  }
  return Number.isFinite(event.startMs) ? event.startMs : Number.NaN;
}

/**
 * Build the reminder set for a list of events at one lead setting.
 *
 * @param previous the entries from the last plan. Their `fired` flags are
 *   CARRIED OVER, which is what stops a replan (a refresh, a tab regaining
 *   focus, an RSVP change) from re-showing a reminder the user already saw.
 * @returns a map keyed by {@link reminderKey}. Keys present in `previous` but
 *   absent here are the ones whose native notifications the caller must cancel.
 */
export function planReminders(
  events: readonly EventVM[],
  lead: ReminderLead,
  now: number,
  previous: Readonly<Record<string, ReminderEntry>> = {},
): Record<string, ReminderEntry> {
  const minutes = leadMinutes(lead);
  // 'off' — and any future lead that means "don't". An empty plan is what makes
  // the runtime cancel every armed native notification.
  if (minutes === null) return {};

  const leadMs = minutes * 60 * 1000;
  const plan: Record<string, ReminderEntry> = {};

  for (const event of events) {
    if (!shouldRemind(event)) continue;

    const startAt = reminderStartMs(event);
    if (!Number.isFinite(startAt)) continue;
    // Only ever remind about something still ahead. An event that already
    // started needs no reminder, and planning one would only give `missed()`
    // work to silence.
    if (startAt <= now) continue;

    const key = reminderKey(event.id, event.occurrenceDate, lead);
    // A recurring series can return the same occurrence twice across page
    // boundaries — first write wins, rather than resetting a carried `fired`.
    if (key in plan) continue;

    plan[key] = {
      key,
      eventId: event.id,
      occurrenceDate: event.occurrenceDate,
      title: event.title,
      startAt,
      fireAt: startAt - leadMs,
      fired: previous[key]?.fired === true,
      nativeId: nativeIdOf(key),
    };
  }

  return plan;
}

/**
 * Reminders that should surface right now: armed, past their fire time, still
 * before the event starts, and not so late that showing them is noise.
 */
export function dueNow(entries: readonly ReminderEntry[], now: number): ReminderEntry[] {
  return entries.filter(
    (entry) =>
      !entry.fired &&
      entry.fireAt <= now &&
      now < entry.startAt &&
      now - entry.fireAt <= REMINDER_GRACE_MS,
  );
}

/**
 * Reminders whose moment has passed — the event has already started, or the
 * fire time is older than the grace window.
 *
 * The caller marks these fired WITHOUT showing anything. A reminder delivered
 * after the meeting began, or hours late because the device was asleep, is
 * worse than no reminder: it reads as a live alert for something already over.
 */
export function missed(entries: readonly ReminderEntry[], now: number): ReminderEntry[] {
  return entries.filter(
    (entry) => !entry.fired && (now >= entry.startAt || now - entry.fireAt > REMINDER_GRACE_MS),
  );
}

/** "45 minutes" / "2 hours" / "1 day" — the tail of the reminder sentence. */
function minutesPhrase(minutes: number): string {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * The one-line body shown in the banner and in the Android notification.
 *
 * Phrased from the REAL remaining time rather than the nominal lead, because a
 * tick can run a minute or two late and "starts in 20 minutes" when it is
 * actually 17 is a small lie the user can check against their own clock. The
 * nominal lead is snapped back in when the two are within a minute of each
 * other, so the common case still reads as the round number the user chose.
 */
export function reminderBody(entry: ReminderEntry, lead: ReminderLead, now: number): string {
  const remaining = Math.round((entry.startAt - now) / 60000);
  if (remaining <= 0) return 'Starting now.';

  const nominal = leadMinutes(lead);
  const minutes = nominal !== null && Math.abs(remaining - nominal) <= 1 ? nominal : remaining;
  return `Starts in ${minutesPhrase(minutes)}.`;
}
